import debounce from "lodash.debounce";
import React, { useCallback, useState } from "react";

interface ComponentSize {
  width: number;
  height: number;
}

function getSize(ref: React.RefObject<HTMLElement>): ComponentSize {
  if (!ref || !ref.current) {
    return {
      width: 0,
      height: 0,
    };
  }

  return {
    width: ref.current.offsetWidth,
    height: ref.current.offsetHeight,
  };
}

export function useElementSize(
  ref: React.RefObject<HTMLElement>,
  throttleDuration: number = 0
): ComponentSize {
  const [state, setState] = useState(getSize(ref));

  function _handleResize() {
    const nextState = getSize(ref);
    const shouldUpdate =
      nextState.width !== state.width || nextState.height !== state.height;

    if (shouldUpdate) {
      setState(nextState);
    }
  }

  const handleResize = useCallback(
    throttleDuration
      ? debounce(_handleResize, throttleDuration)
      : _handleResize,
    [ref, throttleDuration]
  );

  React.useEffect(() => {
    if (!ref || !ref?.current) {
      return;
    }

    handleResize();
    if (typeof ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          handleResize();
        });
      });

      resizeObserver.observe(ref.current);

      return () => {
        resizeObserver.disconnect();
      };
    } else {
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [ref?.current]);

  return state;
}
